import {Button, DirectionalIcon, Divider, Modal} from '@sollapay/ui/components';
import {SaveMoneyIllustration, SecureDocumentIllustration} from '@sollapay/ui/illustrations';
import {useTranslation} from 'react-i18next';

import {ActivationEtaItem, ActivationMetaItem} from '../components';

import type {FC} from 'react';

type SubmitTrustAccountModalState = 'confirm' | 'success';

export type SubmitTrustAccountModalProps = {
  isOpen: boolean;
  state: SubmitTrustAccountModalState;
  onConfirm: () => void;
  onClose: () => void;
  isLoading?: boolean;
};

export const SubmitTrustAccountModal: FC<SubmitTrustAccountModalProps> = ({
  isOpen,
  state,
  onConfirm,
  onClose,
  isLoading = false
}) => {
  const {t} = useTranslation('trustAccounts');
  const isSuccess = state === 'success';

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Modal
      size="sm"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      outsidePress={isSuccess ? 'close' : undefined}
      illustration={isSuccess ? <SaveMoneyIllustration /> : <SecureDocumentIllustration />}
      title={
        isSuccess
          ? t('activation.successSubmitModal.title')
          : t('activation.confirmSubmitModal.title')
      }
      description={
        isSuccess
          ? t('activation.successSubmitModal.description')
          : t('activation.confirmSubmitModal.description')
      }
      footer={
        isSuccess ? (
          <Button variant="primary" size="stretch" onClick={onClose}>
            {t('activation.successSubmitModal.close')}
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="stretch" onClick={onClose}>
              {t('activation.confirmSubmitModal.cancel')}
            </Button>
            <Button
              variant="primary"
              size="stretch"
              onClick={onConfirm}
              isLoading={isLoading}
              icon={<DirectionalIcon name="arrow-forward" />}
              iconPosition="end"
            >
              {t('activation.confirmSubmitModal.confirm')}
            </Button>
          </>
        )
      }
    >
      {isSuccess ? <ActivationSuccessSummaryCard /> : <Divider className="bg-border-subtle" />}
    </Modal>
  );
};

const ActivationSuccessSummaryCard: FC = () => {
  const {t} = useTranslation('trustAccounts');

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-subtle bg-background-secondary p-4.25">
      <ActivationMetaItem
        icon="check-circle"
        tone="success"
        label={t('activation.successSubmitModal.statusLabel')}
        value={t('activation.successSubmitModal.statusValue')}
      />
      <ActivationEtaItem />
    </div>
  );
};
